import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Clock, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { 
  BusinessHoursForm as BusinessHoursFormType, 
  BusinessHours, 
  DayBusinessHours,
  COMMON_TIMEZONES,
  DEFAULT_BUSINESS_HOURS_FORM 
} from '@/services/business/types';

// Validation schema for business hours
const businessHoursSchema = z.object({
  timezone: z.string().min(1, 'Timezone is required'),
  businessHours: z.object({
    monday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
    tuesday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
    wednesday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
    thursday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
    friday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
    saturday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
    sunday: z.object({
      enabled: z.boolean(),
      start: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
      end: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format'),
    }),
  }),
});

interface BusinessHoursFormProps {
  initialData?: BusinessHoursFormType;
  onSubmit: (data: BusinessHoursFormType) => Promise<void>;
  isLoading?: boolean;
}

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
] as const;

export function BusinessHoursForm({ initialData, onSubmit, isLoading = false }: BusinessHoursFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<BusinessHoursFormType>({
    resolver: zodResolver(businessHoursSchema),
    defaultValues: initialData || DEFAULT_BUSINESS_HOURS_FORM,
  });

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      form.reset(initialData);
    }
  }, [initialData, form]);

  const handleSubmit = async (data: BusinessHoursFormType) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      toast.success('Business hours updated successfully');
    } catch (error) {
      console.error('Failed to update business hours:', error);
      toast.error('Failed to update business hours');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    form.reset(initialData || DEFAULT_BUSINESS_HOURS_FORM);
    toast.info('Form reset to original values');
  };

  const copyToAllDays = (sourceDay: keyof BusinessHours) => {
    const sourceHours = form.getValues(`businessHours.${sourceDay}`);
    DAYS_OF_WEEK.forEach(({ key }) => {
      if (key !== sourceDay) {
        form.setValue(`businessHours.${key}.start`, sourceHours.start);
        form.setValue(`businessHours.${key}.end`, sourceHours.end);
      }
    });
    toast.info(`Copied ${sourceDay} hours to all other days`);
  };

  const toggleAllDays = (enabled: boolean) => {
    DAYS_OF_WEEK.forEach(({ key }) => {
      form.setValue(`businessHours.${key}.enabled`, enabled);
    });
    toast.info(enabled ? 'Enabled all days' : 'Disabled all days');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Business Hours
        </CardTitle>
        <CardDescription>
          Set your company's operating hours and timezone. This helps with scheduling and customer expectations.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Timezone Selection */}
            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Timezone</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COMMON_TIMEZONES.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator />

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => toggleAllDays(true)}
              >
                Enable All Days
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => toggleAllDays(false)}
              >
                Disable All Days
              </Button>
            </div>

            {/* Business Hours for Each Day */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Weekly Schedule</h3>
              {DAYS_OF_WEEK.map(({ key, label }) => (
                <div key={key} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center p-4 border rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FormField
                      control={form.control}
                      name={`businessHours.${key}.enabled`}
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-2">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="text-sm font-medium">
                            {label}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name={`businessHours.${key}.start`}
                    render={({ field }) => (
                      <FormItem style={{ display: form.watch(`businessHours.${key}.enabled`) ? 'block' : 'none' }}>
                        <FormLabel className="text-xs text-muted-foreground">Start Time</FormLabel>
                        <FormControl>
                          <Input
                            type="time"
                            {...field}
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`businessHours.${key}.end`}
                    render={({ field }) => (
                      <FormItem style={{ display: form.watch(`businessHours.${key}.enabled`) ? 'block' : 'none' }}>
                        <FormLabel className="text-xs text-muted-foreground">End Time</FormLabel>
                        <FormControl>
                          <Input
                            type="time"
                            {...field}
                            className="w-full"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div style={{ display: form.watch(`businessHours.${key}.enabled`) ? 'block' : 'none' }}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToAllDays(key)}
                      className="text-xs"
                    >
                      Copy to All
                    </Button>
                  </div>

                  <div 
                    className="md:col-span-3 text-sm text-muted-foreground"
                    style={{ display: !form.watch(`businessHours.${key}.enabled`) ? 'block' : 'none' }}
                  >
                    Closed
                  </div>
                </div>
              ))}
            </div>

            {/* Form Actions */}
            <div className="flex justify-between pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleReset}
                disabled={isSubmitting || isLoading}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>

              <Button
                type="submit"
                disabled={isSubmitting || isLoading}
                className="flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}